import { ConfigurationTreeNodeType, IConfigurationTreeNode } from '../types';

declare var InovxLoader: any;

// Resolved per call: module-scope translation runs before InovxLoader.initialize().
export const getTagShapeOptions = () => [
    { id: 'RoundedRectangle', name: InovxLoader.t('facility:form.tagShapeRoundedRectangle') },
    { id: 'Rectangle', name: InovxLoader.t('facility:form.tagShapeRectangle') }
];

export const fontSizeOptions = [8, 9, 10, 11, 12, 14, 16, 18, 20];

export const getFontStyleOptions = () => [
    { name: InovxLoader.t('facility:form.fontStyleRegular'), value: 'Regular' },
    { name: InovxLoader.t('facility:form.fontStyleBold'), value: 'Bold' },
    { name: InovxLoader.t('facility:form.fontStyleItalic'), value: 'Italic' }
];

export const getAttributeDataTypeLabels = (): { [key: number]: string } => ({
    1: InovxLoader.t('facility:form.dataTypeString'),
    2: InovxLoader.t('facility:form.dataTypeDate'),
    3: InovxLoader.t('facility:form.dataTypeLink'),
    4: InovxLoader.t('facility:form.dataTypeBoolean'),
    5: InovxLoader.t('facility:form.dataTypeNumber'),
});

export const getSavingProgressMessage = () => `${InovxLoader.t('facility:form.saving')}.`;

export const getDeletingProgressMessage = () => `${InovxLoader.t('facility:form.deleting')}.`;

export const sortByName = <T extends { Name: string }>(items: T[] | null | undefined): T[] => {
    return [...(items || [])].sort((left, right) => left.Name.localeCompare(right.Name));
};

export const getConfigurationTreeNodeKey = (schema: ISchema, parentSchemaId?: string): string => {
    return schema.IsDependent ? `schema-extension-${schema.ID}-parent-${parentSchemaId}` : `schema-${schema.ID}`;
};

export const findTreeNodeByKey = (nodes: IConfigurationTreeNode[], treeNodeKey: string | null): IConfigurationTreeNode | null => {
    if (!treeNodeKey) {
        return null;
    }

    for (const node of nodes) {
        if (node.TreeNodeKey === treeNodeKey) {
            return node;
        }

        if (node.Children?.length) {
            const childNode = findTreeNodeByKey(node.Children, treeNodeKey);
            if (childNode) {
                return childNode;
            }
        }
    }

    return null;
};

interface IBuildConfigurationTreeNodeArgs {
    schema: ISchema;
    getSchemaExtensions: (primarySchemaId: string | null | undefined) => ISchema[];
    treeAssetClassesBySchema: Record<string, IAssetClassUpdateDetail[]>;
    treeAnnotationClassesBySchema: Record<string, IAssetClassUpdateDetail[]>;
    collapsedTreeNodeKeys: Set<string>;
    parentTreeNodeKey?: string;
    parentSchemaId?: string;
    ancestorSchemaIds?: string[];
}

interface ICollectConfigurationTreeNodeKeysArgs {
    schema: ISchema;
    getSchemaExtensions: (primarySchemaId: string | null | undefined) => ISchema[];
    parentSchemaId?: string;
    ancestorSchemaIds?: string[];
}

export const collectConfigurationTreeNodeKeys = ({
    schema,
    getSchemaExtensions,
    parentSchemaId,
    ancestorSchemaIds = []
}: ICollectConfigurationTreeNodeKeysArgs): string[] => {
    const treeNodeKey = getConfigurationTreeNodeKey(schema, parentSchemaId);
    const nextAncestorSchemaIds = [...ancestorSchemaIds, schema.ID];

    const childTreeNodeKeys = getSchemaExtensions(schema.ID)
        .filter((childSchema) => !nextAncestorSchemaIds.includes(childSchema.ID))
        .flatMap((childSchema) => collectConfigurationTreeNodeKeys({
            schema: childSchema,
            getSchemaExtensions,
            parentSchemaId: schema.ID,
            ancestorSchemaIds: nextAncestorSchemaIds
        }));

    return [treeNodeKey, ...childTreeNodeKeys];
};

export const buildConfigurationTreeNode = ({
    schema,
    getSchemaExtensions,
    treeAssetClassesBySchema,
    treeAnnotationClassesBySchema,
    collapsedTreeNodeKeys,
    parentTreeNodeKey,
    parentSchemaId,
    ancestorSchemaIds = []
}: IBuildConfigurationTreeNodeArgs): IConfigurationTreeNode => {
    const treeNodeKey = getConfigurationTreeNodeKey(schema, parentSchemaId);
    const nextAncestorSchemaIds = [...ancestorSchemaIds, schema.ID];
    const isExpanded = !collapsedTreeNodeKeys.has(treeNodeKey);

    const childSchemaNodes = getSchemaExtensions(schema.ID)
        .filter((childSchema) => !nextAncestorSchemaIds.includes(childSchema.ID))
        .map((childSchema) => buildConfigurationTreeNode({
            schema: childSchema,
            getSchemaExtensions,
            treeAssetClassesBySchema,
            treeAnnotationClassesBySchema,
            collapsedTreeNodeKeys,
            parentTreeNodeKey: treeNodeKey,
            parentSchemaId: schema.ID,
            ancestorSchemaIds: nextAncestorSchemaIds
        }));

    const childAssetClassNodes = sortByName(treeAssetClassesBySchema[schema.ID] || []).map((assetClass) => {
        const primarySchemaAssetClasses = parentSchemaId ? (treeAssetClassesBySchema[parentSchemaId] || []) : [];
        const primaryAssetClass = assetClass.PrimaryAssetClassID
            ? primarySchemaAssetClasses.find((item) => item.ID === assetClass.PrimaryAssetClassID)
            : null;

        return {
        TreeNodeKey: `asset-class-${assetClass.ID}-parent-${treeNodeKey}`,
        ID: assetClass.ID,
        Name: primaryAssetClass?.Name || assetClass.Name,
        NodeType: assetClass.PrimaryAssetClassID ? 'assetClassExtension' as ConfigurationTreeNodeType : 'assetClass' as ConfigurationTreeNodeType,
        SchemaID: schema.ID,
        ParentTreeNodeKey: treeNodeKey,
        AssetClassID: assetClass.ID,
        PrimaryAssetClassID: assetClass.PrimaryAssetClassID,
        IconFileName: assetClass.PrimaryAssetClassID ? 'ClassExtension2.png' : 'AssetClass2.png',
        AssetClass: assetClass
        };
    });

    const childAnnotationClassNodes = sortByName((treeAnnotationClassesBySchema[schema.ID] || []).filter((annotationClass) => !annotationClass.IsSystem)).map((annotationClass) => {
        return {
            TreeNodeKey: `annotation-class-${annotationClass.ID}-parent-${treeNodeKey}`,
            ID: annotationClass.ID,
            Name: annotationClass.Name,
            NodeType: 'annotationClass' as ConfigurationTreeNodeType,
            SchemaID: schema.ID,
            ParentTreeNodeKey: treeNodeKey,
            AssetClassID: annotationClass.ID,
            IconFileName: 'AnnotationClass.png',
            AssetClass: annotationClass
        };
    });

    return {
        TreeNodeKey: treeNodeKey,
        ID: schema.ID,
        Name: schema.Name,
        NodeType: schema.IsDependent ? 'schemaExtension' : 'schema',
        SchemaID: schema.ID,
        ParentTreeNodeKey: parentTreeNodeKey,
        IconFileName: schema.IsDependent ? 'SchemaExtension.png' : 'Schema.png',
        IsExpanded: isExpanded,
        Children: [...childAssetClassNodes, ...childAnnotationClassNodes, ...childSchemaNodes],
        Schema: schema
    };
};

export const getMapClassMessage = (name: string) => InovxLoader.t('facility:message.mapClassToExternalClassNames').replace('__name__', name);